import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import axios from 'axios';
import { toast } from 'react-toastify';
import authService from './api-authorization/AuthorizeService';
import * as DateHelpers from './DateHelpers';
import Invoices from './Invoices';

interface BatchNumber {
    id: number;
    number: number;
    status: string;
    statusId: number;
    description: string;
    isEvent: boolean;
    rowVersionTimestamp: Uint8Array;
    sentDateFormatted: string;
}

interface InvoiceDetails {
    id: number;
    companyId: number;          //job company id
    invoiceCompanyId: number;   //invoice company id => used to send to the correct Reckon
    customerId: number;
    customer: string;
    customerType: string;
    jobId: number;
    siteAddress: string;
    type: string;
    sendTo: string;
    batchNumber: string;
    reckonInvoiceId: string;
    canUpdateReckonInvoiceId: boolean;
    quoteNumber: string;
    hirePeriod: string;
    invoiceDate: string;
    dueDate: string;
    paidDate: string;
    paidDateActual: string;
    amount: string;
    errorMessage: string;
    status: string;
    statusId: number;
    canEdit: boolean;
    canDelete: boolean;
    invoiceStartDate: string;
    invoiceStartDateFormatted: string;
    isDirty: boolean;
    items: InvoiceItems[];
    showDetail: boolean;
    errors: any;
}

interface InvoiceItems {
    itemIndex: number;
    id: number;
    jobInvoiceId: number;
    branchIdent: string;
    item: string;
    itemId: number;
    itemTypeId: number;
    invItemType: string;
    invItemTypeId: number;
    quantityType: string;
    purchaseOrderNumber: string;
    additionalHireRate: string;
    rate: string;
    rateFormatted: string;
    gst: string;
    gstFormatted: string;
    chargeGst: boolean;
    amount: string;
    amountFormatted: string;
    description: string;
    status: number;
    invoiceStartDate: string;
    invoiceEndDate: string;
    hirePeriodDays: string;
    hirePeriod: string;
    dayRate: string;
    weekMonthRate: string;
    invoiceStartDateFormatted: string;
    invoiceEndDateFormatted: string;
    isDirty: boolean;
}

const InvoiceBatchData = (props: RouteComponentProps<{}>) => {
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [loading, setLoading] = React.useState(true);
    const [loadingInvoices, setLoadingInvoices] = React.useState(false);
    const [locking, setLocking] = React.useState(false);
    const [sending, setSending] = React.useState(false);
    const [lockBatchError, setLockBatchError] = React.useState("");
    const [sendBatchError, setSendBatchError] = React.useState("");
    const [selectedBatch, setSelectedBatch] = React.useState({ batchId: -1, batchNumber: 0, batchStatusId: 0, isEvent: false, sentDateFormatted: "" });
    const [moveInvoices, setMoveInvoices] = React.useState({ fromDate: "", toDate: "" });
    const [rvt, setrvt] = React.useState<Uint8Array>();
    const [batchNumbers, setBatchNumbers] = React.useState<BatchNumber[]>([]);
    const [showOpenBatches, setShowOpenBatches] = React.useState(false);
    const [invoices, setInvoices] = React.useState<InvoiceDetails[]>([]);
    const [invoiceTotalFormatted, setInvoiceTotalFormatted] = React.useState("$0.00");
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(200);
    const [totalRecords, setTotalRecords] = React.useState(0);
    const [canEdit, setCanEdit] = React.useState(false);
    const [tab, setTab] = React.useState("Batch");
    const [batchSending, setBatchSending] = React.useState(false);
    const [invoiceSending, setInvoiceSending] = React.useState(false);
    const [sendEventInvoicesOnly, setSendEventInvoicesOnly] = React.useState(false);
    const [eventBatchId, setEventBatchId] = React.useState(0);

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/User/CheckInvoiceBatchPermission?SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var canEditData = res.data.canEdit;
            var canSendBatch = res.data.viewSendInvoiceBatch;
            var canSendEventInvoices = res.data.sendEventInvoices;

            if (canSendBatch || canSendEventInvoices) {
                setCanEdit(canEditData)
                setSendEventInvoicesOnly(canSendEventInvoices);
                setEventBatchId(res.data.eventInvoiceBatchId);
                //enter the page that quickbooks needs to return to, id blank as not used via invoicing
                var page = {
                    page: "invoicebatch",
                    id: 0
                };
                //encode to base 64 so can retrieve it later
                var encoded = window.btoa(JSON.stringify(page));

                axios.get('api/Invoicing/ReckonInvoiceAuthoriseCheck?returnUrl=' + encoded + '&SubId=' + user.sub, {
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                })
                .then(res => {
                    //continue if Quick Books user is authorised
                    if (res.data.isAuthorised) {
                        getBatchNumbers(true, -1, false, canSendEventInvoices);
                    } else {
                        window.location.replace(res.data.reckonLoginUrl)
                    }
                })
                .catch(error => {
                    toast.error(error.message);
                });
            } else {
                //redirect to 403 permission denied
                props.history.push("/accessdenied");
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const getBatchNumbers = async (getInvoices: boolean, existingBatchId: number, showOpen: boolean, eventInvoicesOnly: boolean) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/Invoicing/GetBatchNumbers?SubId=' + user.sub + '&ShowOpen=' + showOpen + '&EventInvoicesOnly=' + eventInvoicesOnly, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            let batchBeingSent = res.data.batchBeingSentToReckon;
            let invoiceBeingSent = res.data.invoiceBeingSentToReckon;
            if (batchBeingSent || invoiceBeingSent) {
                setLoading(false);
                setBatchSending(batchBeingSent);
                setInvoiceSending(invoiceBeingSent);
            } else {

                let batchNos: BatchNumber[] = res.data.invoiceBatchNumbers;

                var updatedBatchId = -1;
                var updatedBatchNo = 0;
                var batchStatus = 0;
                var rowVersion;
                var isEvent = false;
                var sentDate = "";
                if (existingBatchId >= 0) {
                    updatedBatchId = existingBatchId;
                    //find the batch status
                    var batchOption = batchNos.find(b => b.id == existingBatchId);
                    if (batchOption) {
                        batchStatus = batchOption.statusId;
                        rowVersion = batchOption.rowVersionTimestamp;
                        isEvent = batchOption.isEvent;
                        sentDate = batchOption.sentDateFormatted;
                        updatedBatchNo = batchOption.number;
                    }
                } else if (batchNos.length > 0 && getInvoices) {
                    var batchNo = batchNos[0];
                    updatedBatchId = batchNo.id;
                    batchStatus = batchNo.statusId;
                    rowVersion = batchNo.rowVersionTimestamp;
                    isEvent = batchNo.isEvent;
                    sentDate = batchNo.sentDateFormatted;
                    updatedBatchNo = batchNo.number;
                }

                setTab("Batch");
                setBatchNumbers(batchNos);
                setSelectedBatch({ batchId: updatedBatchId, batchNumber: updatedBatchNo, batchStatusId: batchStatus, isEvent: isEvent, sentDateFormatted: sentDate });
                setrvt(rowVersion);
                setLoading(false);
                setBatchSending(false);
                setInvoiceSending(false);
                setLockBatchError("");
                setSendBatchError("");

                if (batchNos.length > 0 && getInvoices) {
                    setLoadingInvoices(true);
                    loadInvoices(updatedBatchId, batchStatus);
                }
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const loadInvoices = async (updatedBatchId: number, updatedBatchStatus: number) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/Invoicing/GetBatchInvoices?batchId=' + updatedBatchId + '&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            let batchBeingSent = res.data.batchBeingSentToReckon;
            let invoiceBeingSent = res.data.invoiceBeingSentToReckon;
            if (batchBeingSent || invoiceBeingSent) {
                setLoading(false);
                setBatchSending(batchBeingSent);
                setInvoiceSending(invoiceBeingSent);
            } else {
                var invoiceDetails = res.data.invoices;

                for (var i = 0; i < invoiceDetails.length; i++) {
                    var invoice = invoiceDetails[i];

                    for (var y = 0; y < invoice.items.length; y++) {
                        invoice.items[y].itemIndex = y;
                        invoice.items[y].amount = invoice.items[y].amount.toFixed(2);
                    }
                    //add blank row if canEdit invoice and user can edit and open batch
                    if (invoice.canEdit && canEdit && updatedBatchStatus === 1) {
                        var newRow: InvoiceItems = {
                            itemIndex: invoice.items.length,
                            id: 0,
                            jobInvoiceId: invoice.id,
                            branchIdent: "",
                            item: "",
                            itemId: -1,
                            itemTypeId: -1,
                            invItemType: "",
                            invItemTypeId: -1,
                            quantityType: "",
                            purchaseOrderNumber: "",
                            additionalHireRate: "0.00",
                            rate: "0.00",
                            rateFormatted: "$0.00",
                            gst: "0.00",
                            gstFormatted: "$0.00",
                            chargeGst: true,
                            amount: "",
                            amountFormatted: "$0.00",
                            description: "",
                            status: 1,
                            invoiceStartDate: "",
                            invoiceEndDate: "",
                            invoiceStartDateFormatted: "",
                            invoiceEndDateFormatted: "",
                            hirePeriodDays: "",
                            hirePeriod: "",
                            dayRate: "",
                            weekMonthRate: "",
                            isDirty: false
                        }
                        invoice.items.push(newRow);
                    }
                }
                setInvoices(invoiceDetails);
                setInvoiceTotalFormatted(res.data.invoiceTotalFormatted);
                setPageSize(res.data.pageSize);
                setTotalRecords(res.data.totalRecords);
                setLoading(false);
                setLoadingInvoices(false);
                setLocking(false);
                setBatchSending(false);
                setInvoiceSending(false);
                setLockBatchError("");
                setSendBatchError("");
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
        
    }

    const getErrorInvoices = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/Invoicing/GetSentErrorInvoices?subId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var invoiceDetails = res.data.invoices;
            for (var i = 0; i < invoiceDetails.length; i++) {
                var invoice = invoiceDetails[i];
                invoice.errors = {};

                for (var y = 0; y < invoice.items.length; y++) {
                    invoice.items[y].itemIndex = y;
                }
            }

            setTab("Error");
            setInvoices(invoiceDetails);
            setPage(1);
            setPageSize(res.data.pageSize)
            setTotalRecords(res.data.totalRecords)
            setLoading(false);
            setLoadingInvoices(false);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const updateInvoiceDetails = (updatedInvoices: InvoiceDetails[]) => {
        setInvoices(updatedInvoices);
    }

    const tabSelected = (tab: string) => {
        if (tab == "Batch") {
            getBatchNumbers(true, -1, false, sendEventInvoicesOnly);
        } else {
            getErrorInvoices();
        }
    }

    const tabChanged = (e: any) => {
        tabSelected(e.target.value);
    }

    const noChange = (e: any) => {
        //no changes as user doesn't actually click on the radio button
    }

    const save = (data: any) => {
        var invoiceDetails: InvoiceDetails[] = data.invoiceDetails;
        if (invoiceDetails.length > 0) {
            saveInvoice(invoiceDetails);
        } else {
            toast.info("No invoices have been changed");
        }
    }

    const saveInvoice = async (invoiceDetails: InvoiceDetails[]) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var invoiceitems = [];
        for (var inv = 0; inv < invoiceDetails.length; inv++) {
            var item = {
                jobId: invoiceDetails[inv].jobId,
                customerId: invoiceDetails[inv].customerId,
                invoiceItems: invoiceDetails[inv].items.filter(i => i.isDirty)
            }
            invoiceitems.push(item);
        }

        var invDetails = {
            invoiceDetails: invoiceitems,
            subId: user.sub
        }

        //save
        axios.post('api/Invoicing/SaveInvoiceItems', invDetails, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setLoadingInvoices(true);
            loadInvoices(selectedBatch.batchId, selectedBatch.batchStatusId);
            toast.success("Invoices Saved");
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const handleBatchChange = (e: any) => {
        var updatedBatchId = e.target.value * 1;
        var batchOption = batchNumbers.find(b => b.id === updatedBatchId);
        setMoveInvoices(prevState => ({ ...prevState, fromDate: "", toDate: "" }));
        setErrors({});
        if (batchOption) {
            setSelectedBatch({ batchId: updatedBatchId, batchNumber: batchOption.number, batchStatusId: batchOption.statusId, isEvent: batchOption.isEvent, sentDateFormatted: batchOption.sentDateFormatted });
            setrvt(batchOption.rowVersionTimestamp);
            setLoadingInvoices(true);

            loadInvoices(e.target.value, batchOption.statusId);
        } else {
            setSelectedBatch({ batchId: -1, batchNumber: 0, batchStatusId: 0, isEvent: false, sentDateFormatted: "" });
            var timestamp = new Uint8Array();
            setrvt(timestamp);
            setInvoices([]);
            setLockBatchError("");
            setSendBatchError("");
        }
    }

    const handleShowOpenCheckboxChange = (e: any) => {
        setShowOpenBatches(e.target.checked)
        setInvoices([]);
        setInvoiceTotalFormatted("$0.00");
        setMoveInvoices(prevState => ({ ...prevState, fromDate: "", toDate: "" }));
        setErrors({});

        getBatchNumbers(false, -1, e.target.checked, sendEventInvoicesOnly);
    }

    const handleMoveDateChange = (e: any) => {
        setMoveInvoices(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
    }

    //download batch to Excel
    const downloadBatch = (e: any) => {
        e.preventDefault();

        downloadBatchToExcel();
    }

    const downloadBatchToExcel = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios('api/Report/ExportInvoiceBatchById?BatchId=' + selectedBatch.batchId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` },
            method: 'GET',
            responseType: 'blob' //Force to receive data in a Blob Format
        })
        .then(res => {
            //DOWNLOAD FILE TO BROWSER
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', "Invoice Batch " + selectedBatch.batchNumber + ".xlsx");
            document.body.appendChild(link);
            link.click();
        })
        .catch(error => {
            toast.error(error.message);
        });
    }
    //end download batch to excel

    //move invoices into an open batch
    const validateMoveInvoices = () => {
        let errorList: any = {};
        let formIsValid = true;

        if (!moveInvoices.fromDate) {
            formIsValid = false;
            errorList["fromDate"] = "From Date is required";
        } else if (!DateHelpers.isValidDate(moveInvoices.fromDate)) {
            formIsValid = false;
            errorList["fromDate"] = "Valid From Date is required";
        }

        if (!moveInvoices.toDate) {
            formIsValid = false;
            errorList["toDate"] = "To Date is required";
        } else if (!DateHelpers.isValidDate(moveInvoices.toDate)) {
            formIsValid = false;
            errorList["toDate"] = "Valid To Date is required";
        } else if (!DateHelpers.compareDate(moveInvoices.fromDate, moveInvoices.toDate)) {
            formIsValid = false;
            errorList["toDate"] = "To Date >= From Date";
        }

        setErrors(errorList);
        return formIsValid;
    }

    const moveInvoicesToNewBatch = () => {
        if (validateMoveInvoices()) {
            moveInvoicesToBatch();
        } else {
            toast.error("Fix validation errors before moving invoices to a new batch!");
        }
    }

    const moveInvoicesToBatch = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var moveInv = {
            batchId: selectedBatch.batchId,
            fromDate: moveInvoices.fromDate,
            toDate: moveInvoices.toDate,
            subId: user.sub
        };

        axios.post('api/Invoicing/MoveInvoices', moveInv, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                var lockError = "";
                errors.map(function (error: any) {
                    toast.error(error.content);
                    lockError += error.content + " ";
                });
            } else {
                toast.success("Invoices have been moved to a new batch.");
                setMoveInvoices(prevState => ({ ...prevState, fromDate: "", toDate: "" }));
                getBatchNumbers(true, selectedBatch.batchId, showOpenBatches, sendEventInvoicesOnly);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }
    //end move invoices into an open batch

    //lock the batch to stop invoices being added, before sending it to Reckon
    const lockBatch = () => {
        if (locking) {
            return;
        }
        setLocking(true);
        setMoveInvoices(prevState => ({ ...prevState, fromDate: "", toDate: "" }));
        setErrors({});

        lockInvoiceBatch();
    }

    const lockInvoiceBatch = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var lockBatch = {
            batchId: selectedBatch.batchId,
            subId: user.sub
        };

        axios.post('api/Invoicing/LockBatch', lockBatch, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                var lockError = "";
                errors.map(function (error: any) {
                    toast.error(error.content);
                    lockError += error.content + " ";
                });
                setLocking(false);
                setLockBatchError(lockError);
            } else {
                toast.success("Invoice Batch has been Locked!");
                
                setShowOpenBatches(false);
                setLocking(false);
                getBatchNumbers(true, selectedBatch.batchId, false, sendEventInvoicesOnly);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const unlockBatch = () => {
        unlockInvoiceBatch();
    }

    const unlockInvoiceBatch = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var lockBatch = {
            batchId: selectedBatch.batchId,
            subId: user.sub
        };

        axios.post('api/Invoicing/UnlockBatch', lockBatch, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                var lockError = "";
                errors.map(function (error: any) {
                    toast.error(error.content);
                    lockError += error.content + " ";
                });
                setLocking(false);
                setLockBatchError(lockError);
            } else {
                toast.success("Invoice Batch has been Unlocked!");

                setShowOpenBatches(false);
                setLocking(false);
                getBatchNumbers(true, selectedBatch.batchId, false, sendEventInvoicesOnly);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //send the batch to Reckon via API
    const sendInvoiceBatchToReckon = () => {
        if (sending) {
            return;
        }
        
        setSending(true);
        setBatchSending(true);
        setMoveInvoices(prevState => ({ ...prevState, fromDate: "", toDate: "" }));
        updateBatchStatusToSending();
    }

    const updateBatchStatusToSending = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var sendToReckon = {
            batchId: selectedBatch.batchId,
            rowVersionTimestamp: rvt,
            subId: user.sub
        };

        axios.post('api/Invoicing/UpdateBatchStatusToSending', sendToReckon, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                var sendError = "";
                errors.map(function (error: any) {
                    toast.error(error.content);
                    sendError += error.content + " ";
                });
                setSendBatchError(sendError);
                setSending(false);
                setBatchSending(false);
                setInvoiceSending(false);
            } else {
                sendInvoiceBatchApi(token ?? "", user.sub, res.data.rowVersionTimestamp);
            }
        })
        .catch(error => {
            toast.error(error.message);
            setSending(false);
        });

        
    }

    const sendInvoiceBatchApi = async (token: string, userSub: string, updatedRvt: Uint8Array[]) => {
        var sendToReckon = {
            batchId: selectedBatch.batchId,
            rowVersionTimestamp: updatedRvt,
            subId: userSub
        };

        axios.post('api/Invoicing/SendInvoicesToReckonApi', sendToReckon, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                var sendError = "";
                errors.map(function (error: any) {
                    toast.error(error.content);
                    sendError += error.content + " ";
                });
                setSendBatchError(sendError);
                setSending(false);
                setBatchSending(false);
                setInvoiceSending(false);
            } else {
                toast.success("Invoice Batch Sent To Reckon - check invoice status!");
                setSending(false);
                setBatchSending(false);
                setInvoiceSending(false);
                setShowOpenBatches(false);
                setSendBatchError("");
                getBatchNumbers(true, selectedBatch.batchId, false, sendEventInvoicesOnly);
            }
        })
        .catch(error => {
            toast.error(error.message);
            setSending(false);
        });
    }

    var invoiceContents = <Invoices invoiceDetails={invoices} invoiceTotalFormatted={invoiceTotalFormatted} showCustType={false} showJobInfo={true} showQuoteNo={false} showHirePeriod={false} showStatus={true} showStartDate={selectedBatch.isEvent} showPaidDate={false} showPaid={false} showSend={tab == "Error"} showRemove={true} showSentTo={false} showReckonInvNo={true} showError={tab == "Error"} showSent={tab == "Error"} canEdit={selectedBatch.batchStatusId === 1 && tab === "Batch" && canEdit && !sending && !batchSending && !invoiceSending} canEditHirePeriod={false} canSend={tab == "Error"} canUpdatePaid={false} page={1} pageSize={pageSize} totalRecords={totalRecords} pageChanged={noChange} beforeValid={updateInvoiceDetails} afterValid={save} saveHirePeriod={noChange} />

    const renderDetails = (
        <div>
            <div className="tabsComponent" id="tab-container">
                <div className="tabsComponent__showDropdown">
                    <label className="input-group" htmlFor="scheduleTab">
                        <span className="label">Invoices</span>
                        <select className="select" id="scheduleTab" value={tab} onChange={tabChanged} >
                            <option value="Batch">Batch</option>
                            <option value="Error">Error</option>
                        </select>
                    </label>
                </div>

                <div className="tabsComponent__tabs tabsComponent__hideTabs">
                    <input type="radio" name="tabs" id="tab1" className="tabsComponent__control" checked={tab == 'Batch'} onChange={(e) => noChange(e)}></input>
                    <label className="tabsComponent__label" htmlFor="tabItem" onClick={() => tabSelected('Batch')}>Batch</label>
                    <input type="radio" name="tabs" id="tab2" className="tabsComponent__control" checked={tab == 'Error'} onChange={(e) => noChange(e)}></input>
                    <label className="tabsComponent__label" htmlFor="tabItem" onClick={() => tabSelected('Error')}>Error</label>
                </div>

                <div className="tabsComponent__content tabsComponent__content--selected">
                    <div className={tab === "Batch" ? "" : "hidden"}>
                        <div className={batchSending ? "newJobPricelist--prepaid" : "hidden"}>A batch is being sent to Reckon. {sending ? "" : "Wait 5 mins and refresh the screen."}</div>
                        <div className={invoiceSending ? "newJobPricelist--prepaid" : "hidden"}>An invoice is being sent to Reckon. Wait 1 minute and refresh the screen.</div>
                        <div className="makeitflexspacebetween">
                            <span>
                                <label className="input-group--inline" htmlFor="batchId">
                                    <span className="label">Batch</span>
                                    <select className="select" id="batchId" name="batchId" value={selectedBatch.batchId} onChange={(e) => handleBatchChange(e)} disabled={sending || batchSending || invoiceSending}>
                                        <option defaultValue="-1"></option>
                                        {batchNumbers.map(batch =>
                                            <option key={batch.id} value={batch.id}>{batch.description}</option>
                                        )};
                                    </select>
                                </label>

                                <label className="input-group--inline marginLeft10" htmlFor="showOpenBatches">
                                    <span className="label">Show Open Only Batches</span>
                                    <input className="checkbox" type="checkbox" name="showOpenBatches" checked={showOpenBatches} onChange={(e) => handleShowOpenCheckboxChange(e)} disabled={sending || batchSending || invoiceSending}></input>
                                </label>

                                <label className={selectedBatch.sentDateFormatted === "" ? "hidden" : "input-group--inline marginLeft10"}>
                                    <span className="label">Sent Date:</span>
                                    <span>{selectedBatch.sentDateFormatted}</span>
                                </label>

                                <a className={selectedBatch.batchId < 1 || invoices.length < 1 || sendEventInvoicesOnly ? "hidden" : "marginLeft20 alignIconCenter"} title="Download Batch to Excel" href="#" onClick={(e) => downloadBatch(e)}>
                                    <i className="fa-solid fa-download edit--icon--large"></i>
                                </a>
                            </span>

                            <span className={selectedBatch.isEvent && selectedBatch.batchStatusId === 1 && invoices.length > 0 && !sending && !batchSending && !invoiceSending ? "alignitemscentre" : "hidden"}>
                                <span>Move invoices from </span>
                                <input type='date' id="fromDate" name="fromDate" className='input marginLeft10' value={moveInvoices.fromDate} onChange={(e) => handleMoveDateChange(e)}></input>
                                <span className="marginLeft10 marginRight10"> to </span>
                                <input type='date' id="toDate" name="toDate" className='input' value={moveInvoices.toDate} onChange={(e) => handleMoveDateChange(e)}></input>
                                <button className="defaultbutton defaultbutton__medium--height marginLeft10" type="button" onClick={moveInvoicesToNewBatch}>Move Invoices</button>
                            </span>
                        </div>
                        <div className="colAlignRight">
                            <span className={errors["fromDate"] ? "label errors " + (errors["toDate"] ? "marginRight30" : "batch-invoice-move--frommargin") : "hidden"}>{errors["fromDate"]}</span>
                            <span className={errors["toDate"] ? "label errors batch-invoice-move--tomargin" : "hidden"}>{errors["toDate"]}</span>
                        </div>
                        <div>
                            <button className={selectedBatch.batchStatusId === 1 && !loadingInvoices && invoices.length > 0 ? "defaultbutton marginBottom10" : "hidden"} type="button" onClick={lockBatch} disabled={locking || sending || batchSending || invoiceSending || eventBatchId == selectedBatch.batchId}>Lock Batch</button>
                            <button className={selectedBatch.batchStatusId === 4 && !loadingInvoices && invoices.length > 0 ? "defaultbutton marginBottom10" : "hidden"} type="button" onClick={unlockBatch} disabled={locking || sending || batchSending || invoiceSending}>Unlock Batch</button>
                            <span className={(lockBatchError || sendBatchError) && !sending ? "label errors" : "hidden"}>{lockBatchError} {sendBatchError}</span>
                        </div>

                        <div className={loadingInvoices ? "" : "hidden" }>
                            Loading...
                        </div>

                        <div className={loadingInvoices ? "hidden" : ""}>
                            {invoiceContents}
                        </div>
                        <div className={invoices.length > 0 ? "" : "hidden"}>
                            <button className="defaultbutton-next marginBottom10" type="button" onClick={sendInvoiceBatchToReckon} disabled={selectedBatch.batchStatusId != 4 || sending || batchSending || invoiceSending || loadingInvoices || eventBatchId == selectedBatch.batchId}>Send Batch To Reckon</button>
                            <span className={sendBatchError ? "label errors" : "hidden"}>{sendBatchError}</span>
                            <div className={sending ? "" : "hidden"}>Sending...</div>
                            <div></div>
                        </div>
                    </div>
                    <div className={tab === "Batch" ? "hidden" : ""}>
                        {invoiceContents}
                    </div>
                </div>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default InvoiceBatchData;